import { useEffect, useState } from 'react'
import type { Dayjs } from 'dayjs'
import { Alert, Button, Empty, Modal, Popconfirm, Skeleton, Upload } from 'antd'
import { CheckOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons'
import { DefaultDateTimeInput, DefaultInputNumbers, DefaultTable, DefaultTextInput } from 'diferpan-common-web'
import type { BaseColumn } from 'diferpan-common-web'
import { useAppMessage } from '@providers'
import { useFinalizarConferenciaMutation, useFollowUpItensOrdemCompraQuery } from '@queries'
import { downloadFile, formatDateTime, resolverMensagemErro } from '@utils'
import {
  CASAS_DECIMAIS_PRECO_COMPRA,
  calcularValorTotal,
  criarEdicaoDoItem,
  montarAtualizacaoDosItens,
  validarItens,
} from '../conferencia-itens'
import type { EdicaoItensOrdemCompra, ItemOrdemCompraEditado } from '../conferencia-itens'
import {
  COLUNAS_ITENS_ORDEM_COMPRA,
  MENSAGENS_IMPORTACAO_PLANILHA,
  MENSAGENS_VALIDACAO,
  TEXTOS_ITENS_ORDEM_COMPRA,
} from '../content'
import { gerarArquivoPlanilhaItens, lerPlanilhaItens } from '../planilha-itens'
import { ResumoOrdemCompra } from './ResumoOrdemCompra'
import { formatarMoeda } from '../formatadores'
import type { FollowUpItemOrdemCompraResponse, FollowUpOrdemCompraResponse } from '../../../types'
import './style.scss'

interface ModalItensOrdemCompraProps {
  ordem: FollowUpOrdemCompraResponse
  aberto: boolean
  onFechar: () => void
  onFechada: () => void
}

const SEM_VALOR = '—'

const EXTENSOES_PLANILHA = '.xlsx,.xls'

const idJustificativa = (idItem: number) => `justificativa-item-follow-up-${idItem}`

const idErroJustificativa = (idItem: number) => `erro-justificativa-item-follow-up-${idItem}`

const idPrevisaoEntrega = (idItem: number) => `previsao-entrega-item-follow-up-${idItem}`

const descreverItem = (item: FollowUpItemOrdemCompraResponse) =>
  item.descricao ?? `item ${item.codigo ?? item.idPreCompraItem}`

export const ModalItensOrdemCompra = ({ ordem, aberto, onFechar, onFechada }: ModalItensOrdemCompraProps) => {
  const message = useAppMessage()
  const [edicoes, setEdicoes] = useState<EdicaoItensOrdemCompra>({})
  const [isImportandoPlanilha, setIsImportandoPlanilha] = useState(false)

  const itensQuery = useFollowUpItensOrdemCompraQuery({ idOrdemCompra: ordem.numero })
  const finalizarMutation = useFinalizarConferenciaMutation()

  useEffect(() => {
    if (itensQuery.error) message.error(resolverMensagemErro(itensQuery.error, TEXTOS_ITENS_ORDEM_COMPRA.erroConsulta))
  }, [itensQuery.error])

  const itens = itensQuery.data ?? []
  const erros = validarItens(itens, edicoes)
  const hasItensInvalidos = Object.keys(erros).length > 0
  const hasItens = itens.length > 0
  const isFinalizando = finalizarMutation.isPending
  const isFinalizarBloqueado = !hasItens || hasItensInvalidos || isFinalizando
  const isAcoesPlanilhaBloqueadas = isFinalizando || isImportandoPlanilha

  // A edição guarda apenas os itens que o usuário tocou; os demais são derivados
  // da resposta da API, sem sincronizar estado a cada carregamento.
  const obterEdicao = (item: FollowUpItemOrdemCompraResponse) =>
    edicoes[item.idPreCompraItem] ?? criarEdicaoDoItem(item)

  const atualizarEdicao = (item: FollowUpItemOrdemCompraResponse, alteracao: Partial<ItemOrdemCompraEditado>) => {
    setEdicoes((edicoesAtuais) => ({
      ...edicoesAtuais,
      [item.idPreCompraItem]: { ...(edicoesAtuais[item.idPreCompraItem] ?? criarEdicaoDoItem(item)), ...alteracao },
    }))
  }

  const handlePrecoCompraChange = (item: FollowUpItemOrdemCompraResponse, valor: number | null) => {
    atualizarEdicao(item, { precoCompra: valor })
  }

  const handleQuantidadeChange = (item: FollowUpItemOrdemCompraResponse, valor: number | null) => {
    atualizarEdicao(item, { quantidade: valor })
  }

  const handleJustificativaChange = (item: FollowUpItemOrdemCompraResponse, valor: string) => {
    atualizarEdicao(item, { justificativa: valor })
  }

  const handlePrevisaoEntregaChange = (item: FollowUpItemOrdemCompraResponse, valor: Dayjs | null) => {
    atualizarEdicao(item, { previsaoEntrega: valor })
  }

  const handleFinalizarClick = async () => {
    if (isFinalizarBloqueado) return

    try {
      await finalizarMutation.mutateAsync({
        idOrdemCompra: ordem.numero,
        itens: montarAtualizacaoDosItens(itens, edicoes),
      })
      message.success(TEXTOS_ITENS_ORDEM_COMPRA.sucessoFinalizacao)
      onFechar()
    } catch (error) {
      message.error(resolverMensagemErro(error, TEXTOS_ITENS_ORDEM_COMPRA.erroFinalizacao))
    }
  }

  const handleGerarExcelClick = () => {
    try {
      downloadFile(gerarArquivoPlanilhaItens(itens, edicoes, ordem.numero))
    } catch {
      message.error(TEXTOS_ITENS_ORDEM_COMPRA.erroGeracaoExcel)
    }
  }

  const importarPlanilha = async (arquivo: File) => {
    setIsImportandoPlanilha(true)

    try {
      const resultado = lerPlanilhaItens(await arquivo.arrayBuffer(), itens, edicoes)

      if (!resultado.sucesso) {
        message.error(MENSAGENS_IMPORTACAO_PLANILHA[resultado.erro])
        return
      }

      setEdicoes((edicoesAtuais) => ({ ...edicoesAtuais, ...resultado.edicoes }))
      message.success(`${TEXTOS_ITENS_ORDEM_COMPRA.sucessoImportacaoExcel} ${resultado.quantidadeItensAtualizados}.`)

      if (resultado.codigosNaoEncontrados.length > 0) {
        message.warning(
          `${TEXTOS_ITENS_ORDEM_COMPRA.codigosNaoEncontradosExcel} ${resultado.codigosNaoEncontrados.join(', ')}.`
        )
      }
    } catch {
      message.error(MENSAGENS_IMPORTACAO_PLANILHA['arquivo-invalido'])
    } finally {
      setIsImportandoPlanilha(false)
    }
  }

  const handleImportarExcelUpload = (arquivo: File) => {
    void importarPlanilha(arquivo)

    return Upload.LIST_IGNORE
  }

  const renderPrecoCompra = (item: FollowUpItemOrdemCompraResponse) => (
    <DefaultInputNumbers
      value={obterEdicao(item).precoCompra}
      onChange={(valor) => handlePrecoCompraChange(item, valor)}
      min={0}
      step={0.01}
      precision={CASAS_DECIMAIS_PRECO_COMPRA}
      width='100%'
      disabled={isFinalizando}
      aria-label={`${COLUNAS_ITENS_ORDEM_COMPRA.precoCompra} de ${descreverItem(item)}`}
    />
  )

  const renderQuantidade = (item: FollowUpItemOrdemCompraResponse) => (
    <DefaultInputNumbers
      value={obterEdicao(item).quantidade}
      onChange={(valor) => handleQuantidadeChange(item, valor)}
      min={0}
      step={1}
      precision={0}
      width='100%'
      disabled={isFinalizando}
      aria-label={`${COLUNAS_ITENS_ORDEM_COMPRA.quantidade} de ${descreverItem(item)}`}
    />
  )

  const renderJustificativa = (item: FollowUpItemOrdemCompraResponse) => {
    const erro = erros[item.idPreCompraItem]
    const hasErro = erro !== undefined

    return (
      <span className={'campo-item-follow-up-class'}>
        <DefaultTextInput
          id={idJustificativa(item.idPreCompraItem)}
          value={obterEdicao(item).justificativa}
          onChange={(valor) => handleJustificativaChange(item, valor)}
          width='100%'
          disabled={isFinalizando}
          aria-label={`${COLUNAS_ITENS_ORDEM_COMPRA.justificativa} de ${descreverItem(item)}`}
          aria-invalid={hasErro}
          aria-describedby={hasErro ? idErroJustificativa(item.idPreCompraItem) : undefined}
        />
        {hasErro && (
          <span
            className={'campo-item-follow-up-class__erro'}
            id={idErroJustificativa(item.idPreCompraItem)}
            role='alert'
          >
            {MENSAGENS_VALIDACAO[erro]}
          </span>
        )}
      </span>
    )
  }

  const renderValorTotal = (item: FollowUpItemOrdemCompraResponse) => {
    const valorTotal = calcularValorTotal(obterEdicao(item))

    return valorTotal === null ? SEM_VALOR : formatarMoeda(valorTotal)
  }

  const renderPrevisaoEntrega = (item: FollowUpItemOrdemCompraResponse) => (
    <span className={'campo-item-follow-up-class'}>
      <label className={'campo-item-follow-up-class__rotulo-oculto'} htmlFor={idPrevisaoEntrega(item.idPreCompraItem)}>
        {`${COLUNAS_ITENS_ORDEM_COMPRA.previsaoEntrega} de ${descreverItem(item)}`}
      </label>
      <DefaultDateTimeInput
        id={idPrevisaoEntrega(item.idPreCompraItem)}
        value={obterEdicao(item).previsaoEntrega}
        onChange={(valor) => handlePrevisaoEntregaChange(item, valor)}
        width='100%'
        disabled={isFinalizando}
      />
    </span>
  )

  const colunas: BaseColumn[] = [
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.codigo,
      dataIndex: 'codigo',
      key: 'codigo',
      width: 110,
      fixed: 'left',
      sorter: (primeiro: FollowUpItemOrdemCompraResponse, segundo: FollowUpItemOrdemCompraResponse) =>
        (primeiro.codigo ?? 0) - (segundo.codigo ?? 0),
    },
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.codigoFabrica,
      dataIndex: 'codigoFabrica',
      key: 'codigoFabrica',
      width: 150,
      searchable: true,
    },
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.descricao,
      dataIndex: 'descricao',
      key: 'descricao',
      width: 280,
      searchable: true,
    },
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.precoCompra,
      key: 'precoCompra',
      width: 170,
      render: (_: unknown, item: FollowUpItemOrdemCompraResponse) => renderPrecoCompra(item),
    },
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.quantidade,
      key: 'quantidade',
      width: 150,
      render: (_: unknown, item: FollowUpItemOrdemCompraResponse) => renderQuantidade(item),
    },
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.justificativa,
      key: 'justificativa',
      width: 320,
      render: (_: unknown, item: FollowUpItemOrdemCompraResponse) => renderJustificativa(item),
    },
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.valorTotal,
      key: 'valorTotal',
      width: 160,
      align: 'right',
      render: (_: unknown, item: FollowUpItemOrdemCompraResponse) => renderValorTotal(item),
    },
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.dataUltimaEntrega,
      dataIndex: 'dataUltimaEntrega',
      key: 'dataUltimaEntrega',
      width: 180,
      render: (valor: string | null) => (valor === null ? SEM_VALOR : formatDateTime(valor)),
    },
    {
      title: COLUNAS_ITENS_ORDEM_COMPRA.previsaoEntrega,
      key: 'previsaoEntrega',
      width: 230,
      render: (_: unknown, item: FollowUpItemOrdemCompraResponse) => renderPrevisaoEntrega(item),
    },
  ]

  const renderAcoesPlanilha = () => (
    <div className={'acoes-planilha-itens-follow-up-class'}>
      <Button
        icon={<DownloadOutlined />}
        className={'botao-follow-up-class acoes-planilha-itens-follow-up-class__botao'}
        disabled={isAcoesPlanilhaBloqueadas}
        onClick={handleGerarExcelClick}
      >
        {TEXTOS_ITENS_ORDEM_COMPRA.gerarExcel}
      </Button>
      <Upload
        accept={EXTENSOES_PLANILHA}
        showUploadList={false}
        maxCount={1}
        disabled={isAcoesPlanilhaBloqueadas}
        beforeUpload={handleImportarExcelUpload}
      >
        <Button
          type='primary'
          icon={<UploadOutlined />}
          className={'botao-follow-up-class'}
          loading={isImportandoPlanilha}
          disabled={isAcoesPlanilhaBloqueadas}
        >
          {TEXTOS_ITENS_ORDEM_COMPRA.importarExcel}
        </Button>
      </Upload>
    </div>
  )

  const renderRodape = () => (
    <div className={'rodape-itens-follow-up-class'}>
      <span className={'rodape-itens-follow-up-class__dica'}>{TEXTOS_ITENS_ORDEM_COMPRA.dicaJustificativa}</span>
      <div className={'rodape-itens-follow-up-class__acoes'}>
        <Button className={'botao-follow-up-class'} disabled={isFinalizando} onClick={onFechar}>
          {TEXTOS_ITENS_ORDEM_COMPRA.fechar}
        </Button>
        <Popconfirm
          title={TEXTOS_ITENS_ORDEM_COMPRA.tituloConfirmacaoFinalizacao}
          description={TEXTOS_ITENS_ORDEM_COMPRA.descricaoConfirmacaoFinalizacao}
          okText={TEXTOS_ITENS_ORDEM_COMPRA.confirmarFinalizacao}
          cancelText={TEXTOS_ITENS_ORDEM_COMPRA.cancelarFinalizacao}
          disabled={isFinalizarBloqueado}
          onConfirm={handleFinalizarClick}
        >
          <Button
            type='primary'
            icon={<CheckOutlined />}
            className={'botao-follow-up-class'}
            loading={isFinalizando}
            disabled={isFinalizarBloqueado}
            aria-label={`${TEXTOS_ITENS_ORDEM_COMPRA.finalizar} da cotação ${ordem.numero}`}
          >
            {TEXTOS_ITENS_ORDEM_COMPRA.finalizar}
          </Button>
        </Popconfirm>
      </div>
    </div>
  )

  return (
    <Modal
      open={aberto}
      title={`${TEXTOS_ITENS_ORDEM_COMPRA.titulo} ${ordem.numero}`}
      width={{ xs: '96vw', xl: '90%' }}
      centered={true}
      maskClosable={!isFinalizando}
      footer={renderRodape()}
      className={'modal-itens-follow-up-class'}
      onCancel={onFechar}
      afterClose={onFechada}
    >
      <ResumoOrdemCompra ordem={ordem} />

      {hasItensInvalidos && (
        <Alert
          type='warning'
          showIcon={true}
          message={TEXTOS_ITENS_ORDEM_COMPRA.resumoPendencias}
          className={'modal-itens-follow-up-class__aviso'}
        />
      )}
      <Skeleton loading={itensQuery.isPending} active paragraph={{ rows: 6 }}>
        {hasItens ? (
          <>
            {renderAcoesPlanilha()}
            <DefaultTable
              baseColumns={colunas}
              dataSource={itens}
              rowKey='idPreCompraItem'
              className={'tabela-itens-follow-up-class'}
              scroll={{ x: 'max-content', y: 'min(52vh, 520px)' }}
              pagination={{ pageSize: 10 }}
            />
          </>
        ) : (
          <Empty description={TEXTOS_ITENS_ORDEM_COMPRA.vazio} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Skeleton>
    </Modal>
  )
}
